import React, { useState, useMemo } from "react";
import styled from "styled-components";
import Table from "common/MuiTable";
import { SubscriberFlowsResult } from "./SubscriberFlows";
import useProfile from "common/hooks/useProfile";
import colors from "common/graphs/colors";
import { useColorMap } from "./livemetrics.utils";

const WideContainer = styled.div`
  width: 100%;
`;

const HeaderDiv = styled.div`
  display: flex;
  padding: 0 !important;
  &.header {
    border-color: transparent;
  }
  & .title {
    flex: 100% 1 1;
    padding-right: 2.5ch;
    text-align: left;
  }
`;

const H4 = styled.h4`
  flex-grow: 1;
  flex: 0% 1 1 !important;
`;

const PauseContainer = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
`;

const Empty = styled.div`
  width: 100%;
  height: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: "AllianceNo2-SemiBold" !important;
`;

const HIGHLIGHT_COLOR = "#3d85c6";

const BASIC_COLUMNS = [
  {
    label: "MBPS-DN",
    id: "MBPS-DN",
    align: "right",
    precision: "fourDecimals",
    type: "NumberColor",
    color: (value) => (value > 0 ? HIGHLIGHT_COLOR : "transparent"),
  },
  {
    label: "MBPS-UP",
    id: "MBPS-UP",
    align: "right",
    precision: "fourDecimals",
    type: "NumberColor",
    color: (value) => (value > 0 ? HIGHLIGHT_COLOR : "transparent"),
  },
  {
    label: "MBYTES-DN",
    id: "MBYTES-DN",
    align: "right",
    precision: "fourDecimals",
    type: "Number",
  },
  {
    label: "MBYTES-UP",
    id: "MBYTES-UP",
    align: "right",
    precision: "fourDecimals",
    type: "Number",
  },
  {
    label: "PROTO",
    id: "PROTO",
    type: "Text",
  },
  {
    label: "ACC-ADDR",
    id: "ACC-ADDR",
    type: "Text",
  },
  {
    label: "APORT",
    id: "APORT",
    align: "right",
    type: "NumberOrNA",
    format: (value) => {
      if (value === "n/a") {
        return "n/a";
      }
      return parseFloat(value);
    },
  },
  {
    label: "INET-ADDR",
    id: "INET-ADDR",
    type: "Text",
  },
  {
    label: "IPORT",
    id: "IPORT",
    align: "right",
    type: "NumberOrNA",
    format: (value) => {
      if (value === "n/a") {
        return "n/a";
      }
      return parseFloat(value);
    },
  },
  {
    label: "ST",
    id: "ST",
    type: "Text",
  },
  {
    label: "LIFETIME",
    id: "LIFETIME",
    type: "Time",
  },
];

const parseResponse = (response) => {
  const [header, ...rows] = response.trim(/\s/).split("\n");
  const headerAttrs = header.split(/\s+/);
  const headerMap = headerAttrs.reduce((acc, attr, index) => {
    acc[index] = attr;
    return acc;
  }, {});
  return rows.reduce((acc, row) => {
    const rowAttrs = row.split(/\s+/);
    const rowObject = rowAttrs.reduce((accRow, attr, index) => {
      accRow[headerMap[index]] = attr;
      return accRow;
    }, {});
    acc.push(rowObject);
    return acc;
  }, []);
};

function LiveSubscriberFlows({
  liveMode,
  setLiveMode,
  response,
  dpiFields,
  target,
  dpiEnabled,
  liveModePaused,
  toggleLiveModePaused,
}) {
  const profile = useProfile() || {};
  const [colorMap, setColorMap] = useColorMap({
    response,
    liveMode,
    dpiFields,
  });

  const rows = parseResponse(response);
  const columns = useMemo(() => {
    return [
      ...(profile.dpiEnabled === true
        ? [
            {
              label: "DPI",
              id: "DPI",
              type: "TextColor",
              color: (value) => colorMap?.[value] || colors[0],
            },
            {
              label: "DOMAIN",
              id: "DOMAIN",
              type: "Text",
              cellClassName: "domain-column"
            },
            {
              label: "FLOW-POLICY",
              id: "RATE-POLICY",
              type: "Text",
              cellClassName: profile.hideRates ? null : "hyperlink-text",
              onClick: profile.hideRates
                ? null
                : (value) => {
                    setLiveMode(false);
                    globalNavigate("viewPolicyFlowEdit", {
                      ...target,
                      returnView: "viewStatusSubscribers",
                      policy: value,
                      dpiEnabled,
                    });
                  },
            },
          ]
        : []),
      ...BASIC_COLUMNS,
    ];
  }, [profile.dpiEnabled, profile.hideRates, JSON.stringify(colorMap)]);

  return (
    <div className="card">
      <div className="body">
        <WideContainer className="live-subscriberflows">
          <HeaderDiv className="header">
            <H4 className="title subtitle margin-0 dpi-table-header">
              Active Flows
            </H4>
            {liveMode && (
              <PauseContainer>
                <button
                  type="button"
                  className="btn btn-default waves-effect shadow-none margin-r-15"
                  onClick={toggleLiveModePaused}
                >
                  <i className="material-icons">
                    {liveModePaused ? "play_arrow" : "pause"}
                  </i>
                  <span> {liveModePaused ? "Resume" : "Pause"} </span>
                </button>
              </PauseContainer>
            )}
          </HeaderDiv>
          {rows.length === 0 ? <Empty>{"No Data Available"}</Empty> : null}
          {rows.length > 0 ? <Table columns={columns} rows={rows} /> : null}
        </WideContainer>
      </div>
    </div>
  );
}
export default LiveSubscriberFlows;
